import { useEffect, useReducer, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'

import { getAdSuggestions, getMediaDimensions } from 'utils/helpers'
import { initStateObject, objectReducer } from 'reducers/default'
import { fetchPageInfos } from 'api/integrations'
import { AssetClass, Platform } from 'api/models'
import { isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'
import { getTotalDisabledVariations } from 'components/Recommendations/Campaign/Context/disabledVariations'
import { countNaturalVariations } from 'domain/adCount/googleAdCount'

const EMPTY_TOTALS = { meta: 0, google: 0 }

// Can't measure items with no url (Youtube videos) or dimensions
async function measureMedia(mediaObject) {
  const isMeasurable = mediaObject?.url || (mediaObject?.width && mediaObject?.height)
  if (!isMeasurable) return { ...mediaObject, ratio: undefined }

  try {
    const { width, height } = await getMediaDimensions(mediaObject)
    return { ...mediaObject, width, height, ratio: height / width }
  } catch {
    return { ...mediaObject, ratio: undefined }
  }
}

function isForPlatform(campaignDetails, platform) {
  return (campaignDetails?.platforms ?? []).includes(platform)
}

export function useAdCreatives(campaignAdsDetails, setAdCreatives, { ready = true } = {}) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [updatedCreatives, setUpdatedCreatives] = useState({})
  const [adSuggestions, setAdSuggestions] = useState({})
  const [totalAdsByPlatform, setTotalAdsByPlatform] = useState(EMPTY_TOTALS)
  const [fbPageInfo, setFbPageInfo] = useReducer(objectReducer, initStateObject)
  const [fbPageDetails, setFbPageDetails] = useState([])
  const previousIntegrationsRef = useRef(null)
  const previousSuggestionsRef = useRef(null)

  useEffect(() => {
    const getInfo = async () => {
      if (
        campaignAdsDetails?.length &&
        !isEqual(campaignAdsDetails, previousIntegrationsRef.current)
      ) {
        previousIntegrationsRef.current = campaignAdsDetails

        const integration_details = campaignAdsDetails[0]?.integration_details ?? []
        const meta_profile = integration_details.find(
          (integration_detail) =>
            integration_detail.asset_class === AssetClass.profile &&
            integration_detail.platform === Platform.facebook,
        )
        const response = await fetchPageInfos(setFbPageInfo, meta_profile?.asset_id)

        setFbPageDetails(
          Array.from({ length: campaignAdsDetails.length }, () => ({ loading: true })),
        )

        campaignAdsDetails.forEach((_campaignDetails, index) => {
          setFbPageDetails((prev) => {
            const updatedPrev = [...prev]
            updatedPrev[index] = {
              content: response ?? {},
              error: response?.error ?? false,
              loading: false,
            }

            return updatedPrev
          })
        })
      }
    }

    getInfo()
  }, [campaignAdsDetails])

  useEffect(() => {
    if (!ready) return

    const signature = (campaignAdsDetails ?? []).concat(fbPageDetails)
    if (isEqual(previousSuggestionsRef.current, signature)) return

    if (!campaignAdsDetails?.length) {
      previousSuggestionsRef.current = signature
      setTotalAdsByPlatform(EMPTY_TOTALS)
      setAdSuggestions({ regular: [], reels: [] })
      setAdCreatives({ totalAds: 0, adSuggestions: { regular: [], reels: [] } })
      setLoading(false)
      return
    }

    const constructCreative = async () => {
      const newCreatives = await Promise.all(
        campaignAdsDetails.map(async function (campaignDetails) {
          const media = await Promise.all(
            (campaignDetails?.creatives?.media ?? []).map(measureMedia),
          )

          // Also process placement_media to calculate dimensions and ratio for grouped reels
          const placement_media = await Promise.all(
            (campaignDetails?.creatives?.placement_media ?? []).map(measureMedia),
          )

          return {
            ...campaignDetails?.creatives,
            media,
            placement_media,
          }
        }),
      )

      // getAdSuggestions only ever builds Meta placements, so a Google-only TC
      // must not reach it — and the page-info list it reads by index has to
      // stay aligned with the creatives it's given.
      const metaCreatives = []
      const metaPageDetails = []
      newCreatives.forEach((creative, index) => {
        if (!isForPlatform(campaignAdsDetails[index], Platform.facebook)) return
        metaCreatives.push(creative)
        metaPageDetails.push(fbPageDetails[index])
      })

      const [regular, reels] = getAdSuggestions(metaCreatives, metaPageDetails, t)
      const metaTotal =
        regular.length + reels.length - getTotalDisabledVariations(metaCreatives, reels)

      const googleTotal = newCreatives.reduce((sum, creative, index) => {
        if (!isForPlatform(campaignAdsDetails[index], Platform.google)) return sum
        return sum + countNaturalVariations(creative, campaignAdsDetails[index]?.campaignType)
      }, 0)

      setUpdatedCreatives(newCreatives)
      setTotalAdsByPlatform({ meta: metaTotal, google: googleTotal })
      setAdSuggestions({ regular, reels })
      setAdCreatives({ totalAds: metaTotal + googleTotal, adSuggestions: { regular, reels } })
      setLoading(false)
      previousSuggestionsRef.current = signature
    }

    constructCreative().catch((error) => {
      // Avoid leaving the loading state unresolved if an error occurs.
      Sentry.captureException(error)
      setLoading(false)
    })
  }, [campaignAdsDetails, fbPageDetails, setAdCreatives, t, ready])

  return {
    updatedCreatives,
    totalAds: totalAdsByPlatform.meta + totalAdsByPlatform.google,
    totalAdsByPlatform,
    adSuggestions,
    fbPageInfo: fbPageInfo,
    loading,
  }
}
